import React, { useCallback, useState, type KeyboardEvent } from 'react';
import { Breadcrumb, type BreadcrumbSegment } from '@aibuild/ui';
import {
  CrumbRenameInput,
  CrumbsHost,
} from './ProcessEditorBreadcrumb.styled';

export interface ProcessEditorBreadcrumbProps {
  /** Current process name shown as the trailing crumb. */
  processName: string;
  /** Click handler for the "Processes" crumb — usually `() => navigate('/processes')`. */
  onBackToList: () => void;
  /**
   * Commit a new name for the process. Only invoked when the user picks a
   * different non-empty trimmed value. When omitted the title stays
   * read-only even on draft processes (e.g. while a test run is active and
   * the editor is frozen).
   */
  onCommitRename?: (newName: string) => void;
  dataTestId?: string;
}

interface RenameEditorProps {
  initialName: string;
  ariaLabel: string;
  onCommit: (trimmed: string) => void;
  onCancel: () => void;
  dataTestId: string;
  /**
   * Stamped on the input wrapper so screen readers still announce the
   * inline editor as the current breadcrumb page even though we've
   * swapped the default `<button>` segment out via the render slot.
   */
  ariaCurrent: 'page' | undefined;
}

/**
 * Inline rename editor — owns its own draft state so the input can
 * initialise from `initialName` on mount, and unmounting on commit /
 * cancel cleans up automatically. Mirrors the gallery row's rename
 * input so the two surfaces feel consistent.
 */
const RenameEditor: React.FC<RenameEditorProps> = ({
  initialName,
  ariaLabel,
  onCommit,
  onCancel,
  dataTestId,
  ariaCurrent,
}) => {
  const [draft, setDraft] = useState(initialName);

  const commitIfChanged = () => {
    const trimmed = draft.trim();
    if (!trimmed || trimmed === initialName) {
      onCancel();
      return;
    }
    onCommit(trimmed);
  };

  return (
    <CrumbRenameInput
      ref={(el) => {
        if (!el) return;
        // Autofocus + select-all on mount via ref callback so the focus
        // call is inline with the DOM lifecycle.
        if (document.activeElement !== el) {
          el.focus();
          el.select();
        }
      }}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === 'Enter') {
          e.preventDefault();
          commitIfChanged();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          onCancel();
        }
      }}
      onBlur={commitIfChanged}
      maxLength={100}
      aria-label={ariaLabel}
      aria-current={ariaCurrent}
      data-testid={dataTestId}
    />
  );
};

/**
 * Composite "left slot" for the process editor's top bar — renders the
 * `Processes › <name>` trail using the @aibuild/ui Breadcrumb molecule.
 * The trailing `<name>` segment is inline-editable for draft processes:
 * click / Enter / F2 to edit, Enter to commit, Esc / blur-with-no-change
 * to cancel. Persistence is the page's responsibility (see
 * `onCommitRename`).
 */
const ProcessEditorBreadcrumb: React.FC<ProcessEditorBreadcrumbProps> = ({
  processName,
  onBackToList,
  onCommitRename,
  dataTestId = 'process-editor-breadcrumb',
}) => {
  const [isRenaming, setIsRenaming] = useState(false);

  // Editable when the page wired up a rename handler. The page omits
  // the handler during runs so rename is locked then.
  const isEditable = Boolean(onCommitRename);

  const onStartRename = useCallback(() => {
    if (!isEditable) return;
    setIsRenaming(true);
  }, [isEditable]);

  const onCommit = useCallback(
    (trimmed: string) => {
      setIsRenaming(false);
      onCommitRename?.(trimmed);
    },
    [onCommitRename],
  );

  const onCancel = useCallback(() => setIsRenaming(false), []);

  // Custom onKeyDown for the back-crumb: the lib's Segment is a real
  // <button>, so Enter fires onClick natively, but Space on a button
  // also activates it natively — no extra handler needed here. We just
  // expose the testid override so the existing consumer-test contract
  // (`__back`) stays stable.
  const backSegment: BreadcrumbSegment = {
    id: 'list',
    label: 'Processes',
    onClick: onBackToList,
    dataTestId: `${dataTestId}__back`,
  };

  let currentSegment: BreadcrumbSegment;
  if (isRenaming) {
    currentSegment = {
      id: 'current',
      label: processName,
      current: true,
      render: ({ ariaCurrent }) => (
        <RenameEditor
          initialName={processName}
          ariaLabel={`Rename ${processName}`}
          onCommit={onCommit}
          onCancel={onCancel}
          dataTestId={`${dataTestId}__rename-input`}
          ariaCurrent={ariaCurrent}
        />
      ),
    };
  } else if (isEditable) {
    currentSegment = {
      id: 'current',
      label: processName,
      current: true,
      onClick: onStartRename,
      ariaLabel: `Rename process: ${processName}`,
      dataTestId: `${dataTestId}__current`,
    };
  } else {
    currentSegment = {
      id: 'current',
      label: processName,
      current: true,
      dataTestId: `${dataTestId}__current`,
    };
  }

  // F2 shortcut on the editable current segment — power-user parity
  // with the file-explorer / spreadsheet rename convention. The lib's
  // <Segment> doesn't expose onKeyDown per-segment, so we attach a
  // capturing keydown listener on the breadcrumb root via a wrapper
  // div. Cheap, no extra props.
  const onRootKey = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== 'F2' || !isEditable || isRenaming) return;
      const target = event.target as HTMLElement | null;
      if (target?.dataset.testid === `${dataTestId}__current`) {
        event.preventDefault();
        onStartRename();
      }
    },
    [dataTestId, isEditable, isRenaming, onStartRename],
  );

  return (
    <CrumbsHost onKeyDown={onRootKey}>
      <Breadcrumb
        ariaLabel="Breadcrumb"
        dataTestId={dataTestId}
        segments={[backSegment, currentSegment]}
      />
    </CrumbsHost>
  );
};

export default ProcessEditorBreadcrumb;
